// mt-home.js — interactivity for the redesigned home page.
// Vanilla JS port of the React behaviors: hero photo rotator, FAQ accordion,
// edition tabs (guidebook + gallery), pull-card demo, live ticker, urgency timer,
// sticky ATC visibility, newsletter submit, cart upsell helpers.
// All behaviors are self-contained, null-safe, and idempotent.

(function () {
  'use strict';

  function onReady(fn) {
    if (document.readyState !== 'loading') fn();
    else document.addEventListener('DOMContentLoaded', fn);
  }

  // ========================================================================
  // Fire tweak exposure event for analytics
  // ========================================================================
  onReady(function () {
    var t = window.MT_TWEAKS || null;
    if (!t) return;
    try {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ event: 'mt_home_tweaks', mt_tweaks: t });
    } catch (e) {}
    try { if (typeof window.fbq === 'function') window.fbq('trackCustom', 'MTHomeVariant', t); } catch (e) {}
  });

  // ========================================================================
  // fireMetaOnce — session-deduplicated GA4 + (selective) Meta event helper.
  //
  // Use for engagement signals that fire from JS state (not button clicks —
  // for those use `data-meta-event` attribute). Each `key` fires AT MOST
  // ONCE per browser session.
  //
  // META RULE (added 2026-05-13 in response to Meta Events Manager
  // "improve event coverage" warning):
  //
  //   ViewContent events fired from theme code DO NOT go to Meta. They go
  //   to GA4 only.
  //
  // Why: Shopify's auto-Pixel + CAPI fires ViewContent on product page
  // loads — the real, optimizable signal. Theme-fired ViewContents (tab
  // switches, pull-card reveals) are engagement noise. Sending them to
  // Meta inflates Pixel's ViewContent count beyond what CAPI sees, hurts
  // event-coverage match rate, and pollutes Meta's optimization signal.
  // GA4 + MS Clarity are the right home for engagement analysis.
  //
  // All other event names (Contact, Lead, AddToCart, Purchase, etc.)
  // continue to fire to BOTH Meta and GA4.
  // ========================================================================
  var __metaFired = (function () {
    try { return JSON.parse(sessionStorage.getItem('mt_meta_fired') || '{}'); }
    catch (e) { return {}; }
  })();
  function fireMetaOnce(key, eventName, payload) {
    if (__metaFired[key]) return;
    __metaFired[key] = 1;
    try { sessionStorage.setItem('mt_meta_fired', JSON.stringify(__metaFired)); } catch (e) {}
    // Skip Meta for ViewContent — see META RULE comment above.
    if (eventName !== 'ViewContent') {
      try {
        if (typeof window.fbq === 'function') window.fbq('track', eventName, payload || {});
      } catch (e) {}
    }
    try {
      if (typeof window.gtag === 'function') {
        var gaName = 'mt_' + String(eventName).replace(/([a-z])([A-Z])/g, '$1_$2').toLowerCase();
        window.gtag('event', gaName, payload || {});
      }
    } catch (e) {}
  }
  // Expose for debugging
  window.MT_METRICS = { fireMetaOnce: fireMetaOnce, fired: __metaFired };

  // ========================================================================
  // Hero photo rotator — crossfade every 8s
  //
  // Image 1 (the LCP) is preloaded via <link rel="preload"> in mt-v2-head.liquid
  // with fetchpriority=high. Images 2 and 3 had `new Image()` preloads firing
  // synchronously here — that competed with the LCP fetch and inflated mobile
  // LCP. Defer them to requestIdleCallback so they fetch only after the page
  // has settled (CA9). Browsers will also start fetching them when the rotator
  // tries to crossfade in (8s mark) if idle hasn't fired by then.
  // ========================================================================
  onReady(function () {
    document.querySelectorAll('[data-mt-photo-rotator]').forEach(function (root) {
      var imgs = root.querySelectorAll('.mt-hero__photo');
      var dots = root.querySelectorAll('.mt-hero__photo-dot');
      if (imgs.length < 2) return;
      var i = 0;
      // Defer preload of images 2..N until the browser is idle — image 1 is
      // already the LCP and gets fetched eagerly via the <link rel="preload">.
      var preloadRest = function () {
        imgs.forEach(function (img, idx) {
          if (idx === 0) return;
          var p = new Image();
          // Use the largest srcset variant if present so the cached fetch can
          // serve any later viewport size. Falls back to plain src otherwise.
          var srcset = img.getAttribute('srcset');
          if (srcset) {
            var parts = srcset.split(',').map(function (s) { return s.trim(); });
            var last = parts[parts.length - 1].split(/\s+/)[0];
            p.src = last;
          } else {
            p.src = img.getAttribute('src');
          }
        });
      };
      if (typeof window.requestIdleCallback === 'function') {
        window.requestIdleCallback(preloadRest, { timeout: 4000 });
      } else {
        setTimeout(preloadRest, 2500);
      }
      setInterval(function () {
        i = (i + 1) % imgs.length;
        imgs.forEach(function (img, idx) { img.classList.toggle('is-active', idx === i); });
        dots.forEach(function (d,   idx) { d.classList.toggle('is-active', idx === i); });
      }, 8000);
    });
  });

  // ========================================================================
  // Urgency launch timer
  // ========================================================================
  onReady(function () {
    document.querySelectorAll('[data-mt-launch-timer]').forEach(function (el) {
      var target = Date.now() + (2 * 86400 + 14 * 3600 + 22 * 60) * 1000;
      var dEl = el.querySelector('[data-d]');
      var hEl = el.querySelector('[data-h]');
      var mEl = el.querySelector('[data-m]');
      var sEl = el.querySelector('[data-s]');
      function pad(n) { return String(n).padStart(2, '0'); }
      function tick() {
        var diff = Math.max(0, target - Date.now());
        var d = Math.floor(diff / 86400000);
        var h = Math.floor((diff % 86400000) / 3600000);
        var m = Math.floor((diff % 3600000) / 60000);
        var s = Math.floor((diff % 60000) / 1000);
        if (dEl) dEl.textContent = pad(d);
        if (hEl) hEl.textContent = pad(h);
        if (mEl) mEl.textContent = pad(m);
        if (sEl) sEl.textContent = pad(s);
      }
      tick();
      setInterval(tick, 1000);
    });
  });

  // ========================================================================
  // Screen readers: decorative symbols stay quiet. The page uses ✦ ★ → ↗ ▾ as ornaments in eyebrows, bullets and links;
  // read aloud they become "black four pointed star" between every line. Each one is wrapped in aria-hidden, after the
  // A/B harness has written its text and again on load (the harness may rewrite a line; the text itself never changes).
  // ========================================================================
  (function () {
    var MARKS = /[\u2726\u2605\u2192\u2197\u25BE]/;
    function quiet() {
      var root = document.querySelector('[data-mt-home]'); if (!root) return;
      var walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
        acceptNode: function (n) {
          if (!MARKS.test(n.nodeValue)) return NodeFilter.FILTER_REJECT;
          var p = n.parentElement;
          if (!p || p.closest('script, style, [aria-hidden="true"], [aria-label], [role="img"]')) return NodeFilter.FILTER_REJECT;
          return NodeFilter.FILTER_ACCEPT;
        }
      });
      var nodes = []; while (walker.nextNode()) nodes.push(walker.currentNode);
      nodes.forEach(function (n) {
        var frag = document.createDocumentFragment();
        n.nodeValue.split(/([\u2726\u2605\u2192\u2197\u25BE])/).forEach(function (part) {
          if (!part) return;
          if (MARKS.test(part)) { var s = document.createElement('span'); s.setAttribute('aria-hidden', 'true'); s.textContent = part; frag.appendChild(s); }
          else frag.appendChild(document.createTextNode(part));
        });
        n.parentNode.replaceChild(frag, n);
      });
    }
    onReady(function () { setTimeout(quiet, 0); });
    window.addEventListener('load', quiet);
  })();

  // ========================================================================
  // FAQ accordion
  // ========================================================================
  onReady(function () {
    // Screen readers: the button says whether its answer is open, and a closed answer is inert (not read, its link not
    // tabbable) so the list reads as questions until one is opened.
    function setOpen(item, open) {
      item.classList.toggle('is-open', open);
      var btn = item.querySelector('.mt-faq__q'), ans = item.querySelector('.mt-faq__a');
      if (btn) btn.setAttribute('aria-expanded', open ? 'true' : 'false');
      if (ans) { if (open) ans.removeAttribute('inert'); else ans.setAttribute('inert', ''); }
    }
    var items = document.querySelectorAll('.mt-faq__item');
    items.forEach(function (item) {
      var btn = item.querySelector('.mt-faq__q');
      if (!btn) return;
      setOpen(item, item.classList.contains('is-open'));
      btn.addEventListener('click', function () {
        var was = item.classList.contains('is-open');
        items.forEach(function (i) { setOpen(i, false); });
        if (!was) setOpen(item, true);
      });
    });
  });

  // ========================================================================
  // Guidebook edition tabs
  // ========================================================================
  onReady(function () {
    var root = document.querySelector('[data-mt-guidebook]');
    if (!root) return;
    var data = (window.MT_DATA && window.MT_DATA.guidebook) || null;
    if (!data) return;
    var tabs = root.querySelectorAll('[data-gb-tab]');
    var wrap = root.querySelector('.mt-gb__wrap');
    var book = root.querySelector('.mt-gb__book');
    var romanEl   = root.querySelector('.mt-gb__page-roman');
    var titleEl   = root.querySelector('.mt-gb__page-title');
    var tradEl    = root.querySelector('.mt-gb__page-trad');
    var pillEl    = root.querySelector('.mt-gb__pill');
    var sectionsEl= root.querySelector('[data-gb-sections]');
    var hashtagsEl= root.querySelector('.mt-gb__hashtags');
    var subEl     = root.querySelector('[data-gb-sub]');
    var listEl    = root.querySelector('[data-gb-list]');
    var hintEl    = root.querySelector('[data-gb-hint]');
    var captionEl = root.querySelector('.mt-gb__caption');

    // First render is synchronous (no fade). Subsequent tab clicks fade through
    // .is-swapping → swap content while invisible → fade back in. Avoids the
    // visible "stretch" caused by the layout reflow on .innerHTML swap.
    var firstRender = true;

    function applyRender(edition) {
      var page = data[edition];
      if (!page) return;
      tabs.forEach(function (t) {
        var isActive = t.getAttribute('data-gb-tab') === edition;
        t.classList.toggle('is-active-mt', isActive && edition === 'mt');
        t.classList.toggle('is-active-gz', isActive && edition === 'gzt');
      });
      if (wrap) { wrap.classList.remove('mt', 'gzt'); wrap.classList.add(edition); }
      if (book) { book.classList.remove('mt', 'gzt'); book.classList.add(edition); }
      if (romanEl) romanEl.textContent = page.roman;
      if (titleEl) titleEl.textContent = page.title;
      if (tradEl)  tradEl.textContent  = page.aka;
      if (pillEl)  pillEl.textContent  = edition === 'mt' ? 'Millennial' : 'Gen Z';
      if (sectionsEl) {
        sectionsEl.innerHTML = page.sections.map(function (s) {
          return '<div class="mt-gb__section"><div class="mt-gb__kicker">' +
            escapeHtml(s.kicker) + '</div><p class="mt-gb__body">' +
            escapeHtml(s.body) + '</p></div>';
        }).join('');
      }
      if (hashtagsEl) {
        if (page.hashtags && page.hashtags.length) {
          hashtagsEl.style.display = 'flex';
          hashtagsEl.innerHTML = page.hashtags.map(function (h) { return '<span>' + escapeHtml(h) + '</span>'; }).join('');
        } else {
          hashtagsEl.style.display = 'none';
          hashtagsEl.innerHTML = '';
        }
      }
      if (subEl) {
        subEl.innerHTML = edition === 'mt'
          ? '132+ pages, written by someone who\'s been to therapy. Every card is a real-life situation — translated into plain, honest, slightly funny English. No robes. No riddles. No "consult your inner priestess."'
          : '112 pages, written in the voice of the group chat. Every card, translated into how you actually talk — a little unhinged, a little therapized, and deeply online. No incense required.';
      }
      if (listEl) {
        var extras = edition === 'mt'
          ? '<li><strong>"The Sitch."</strong> What the card is actually saying about your week.</li>' +
            '<li><strong>"Real Talk."</strong> The deeper guidance — what to do with it, said plainly.</li>' +
            '<li><strong>"Reverse, Reverse."</strong> When the vibe flips, we tell you exactly how.</li>'
          : '<li><strong>"TFW."</strong> What the card is actually saying about your week.</li>' +
            '<li><strong>"On Periodt."</strong> The deeper guidance — the advice, unfiltered.</li>' +
            '<li><strong>"Hits Different."</strong> When the vibe flips, we tell you exactly how.</li>';
        listEl.innerHTML =
          '<li><strong>"TL;DR"</strong> The whole reading in one line if that\'s all you need.</li>' +
          extras;
      }
      if (hintEl) {
        hintEl.innerHTML = '✦ You\'re reading the <strong>' + (edition === 'mt' ? 'Millennial' : 'Gen Z') +
          '</strong> guidebook — same card XII, different voice from cover to cover.';
      }
      if (captionEl) captionEl.textContent = 'One page of ' + (edition === 'mt' ? '132+' : '112') + '. Every card works like this.';
    }

    // Outer render — handles the fade orchestration.
    function render(edition) {
      if (firstRender || !wrap) {
        applyRender(edition);
        firstRender = false;
        return;
      }
      // Fade out → swap content while invisible → fade back in.
      // 180ms matches the CSS transition on .mt-gb__wrap content blocks.
      wrap.classList.add('is-swapping');
      setTimeout(function () {
        applyRender(edition);
        // Force a reflow tick so the browser registers the new content
        // before the opacity transition runs back to 1.
        requestAnimationFrame(function () {
          wrap.classList.remove('is-swapping');
        });
      }, 180);
    }

    tabs.forEach(function (t) {
      t.addEventListener('click', function () {
        var ed = t.getAttribute('data-gb-tab');
        render(ed);
        // Engagement signal: visitor compared editions in the guidebook.
        // Fires once per session on first edition switch (any direction).
        fireMetaOnce('guidebook_edition_switch', 'ViewContent', {
          content_name: 'guidebook_compare',
          content_category: 'engagement',
          content_ids: [ed]
        });
      });
    });
    // The brand's own deck shows first (Gen Z on genztarot.com); see window.MT_BRAND in mt-v2-head.liquid.
    render((window.MT_BRAND && window.MT_BRAND.leadDeck) || 'mt');
  });

  // ========================================================================
  // Card gallery edition tabs
  // ========================================================================
  onReady(function () {
    var root = document.querySelector('[data-mt-gallery]');
    if (!root) return;
    var data = window.MT_DATA || {};
    var tabs = root.querySelectorAll('[data-cg-tab]');
    var grid = root.querySelector('[data-cg-grid]');
    if (!grid || !data.mtCards) return;

    var demoById = {};
    (data.demoCards || []).forEach(function (c) { demoById[c.id] = c; });

    // Edition card-back URLs for the click-to-flip tiles (set as data attrs
    // on the section root by mt-card-gallery.liquid).
    var BACK_SRC = {
      mt:  root.getAttribute('data-back-mt') || '',
      gzt: root.getAttribute('data-back-gz') || ''
    };

    function renderTile(card, edition) {
      var isGZ = edition === 'gzt';
      var gzArt = isGZ ? (data.gzArt && data.gzArt[card.id]) : null;
      var demo  = demoById[card.id];
      var displayName = isGZ ? ((gzArt && gzArt.name) || (demo && demo.gz) || card.mtName) : card.mtName;
      var displayRoman = isGZ && gzArt && gzArt.roman ? gzArt.roman : card.roman;
      var displayTrad  = isGZ && gzArt && gzArt.trad  ? gzArt.trad  : card.tradName;
      var blurb = isGZ && demo && demo.readingGz ? demo.readingGz : card.blurb;

      var imgHtml;
      if (isGZ && gzArt) {
        imgHtml = '<img class="mt-gallery__tile-img" src="' + gzArt.img + '" alt="' + escapeHtml(displayName) + '">';
      } else if (isGZ) {
        imgHtml = '<div class="mt-gallery__gz-front"><div class="mt-gallery__gz-roman">' + escapeHtml(displayRoman) +
          '</div><div class="mt-gallery__gz-name">' + escapeHtml(displayName) +
          '</div><div class="mt-gallery__gz-trad">' + escapeHtml(displayTrad) + '</div></div>';
      } else {
        imgHtml = '<img class="mt-gallery__tile-img" src="' + card.img + '" alt="' + escapeHtml(card.mtName) + '">';
      }

      var captionTop = displayRoman
        ? escapeHtml(displayRoman) + ' &mdash; ' + escapeHtml(displayTrad)
        : escapeHtml(displayTrad);
      // Structure must stay in lockstep with the SSR tiles in
      // mt-card-gallery.liquid. Flip state lives in a class, so rebuilding
      // grid.innerHTML on edition switch resets every card face-up — the
      // back always matches the front's edition.
      var backSrc = BACK_SRC[isGZ ? 'gzt' : 'mt'];
      return '<div class="mt-gallery__tile mt-flip" data-mt-flip role="button" tabindex="0" aria-pressed="false" aria-label="' + escapeHtml(displayName) + ' — tap to flip the card over">' +
        '<div class="mt-gallery__tile-inner mt-flip__inner">' +
          '<div class="mt-flip__face mt-flip__face--front">' + imgHtml +
            '<div class="mt-gallery__tile-overlay"><div class="mt-gallery__tile-overlay-body">' +
              '<div class="mt-gallery__tile-trad">' + escapeHtml(displayTrad) + '</div>' +
              '<div class="mt-gallery__tile-blurb">"' + escapeHtml(blurb || '') + '"</div>' +
            '</div></div>' +
          '</div>' +
          '<div class="mt-flip__face mt-flip__face--back">' +
            '<img class="mt-gallery__tile-back" src="' + backSrc + '" alt="' + (isGZ ? 'Gen Z' : 'Millennial') + ' Tarot card back" loading="lazy" decoding="async">' +
          '</div>' +
        '</div>' +
        '<div class="mt-gallery__tile-caption">' +
          '<div class="mt-gallery__tile-roman' + (isGZ ? ' gz' : '') + '">' + captionTop + '</div>' +
          '<div class="mt-gallery__tile-name">' + escapeHtml(displayName) + '</div>' +
        '</div>' +
      '</div>';
    }

    function render(edition) {
      tabs.forEach(function (t) {
        var isActive = t.getAttribute('data-cg-tab') === edition;
        t.classList.toggle('is-active-mt', isActive && edition === 'mt');
        t.classList.toggle('is-active-gz', isActive && edition === 'gzt');
      });
      grid.innerHTML = (data.mtCards || []).map(function (c) { return renderTile(c, edition); }).join('');
    }

    tabs.forEach(function (t) {
      t.addEventListener('click', function () {
        var ed = t.getAttribute('data-cg-tab');
        render(ed);
        // Engagement signal: visitor compared editions in the card gallery.
        fireMetaOnce('gallery_edition_switch', 'ViewContent', {
          content_name: 'card_gallery_compare',
          content_category: 'engagement',
          content_ids: [ed]
        });
      });
    });
    render((window.MT_BRAND && window.MT_BRAND.leadDeck) || 'mt');
  });

  // ========================================================================
  // Card flip — [data-mt-flip] click/keyboard toggle. Used by the
  // problem/solution after-cards and the gallery tiles. CSS does the 3D
  // (.mt-flip machinery in mt-home.css); this just flips the state class.
  // Delegated so gallery tiles re-rendered by the edition tabs keep working —
  // and since renderTile rebuilds grid.innerHTML on every edition switch,
  // flipped cards naturally reset when the edition changes.
  // ========================================================================
  (function mtFlip() {
    // Cumulative angle, never reversed: every spin adds +180°, so the
    // auto-return continues in the SAME direction (…180 → 360 → 540…)
    // instead of unwinding. The inline transform overrides the CSS rest
    // state; parity (odd = back showing) drives the state class + aria.
    var AUTO_RETURN_MS = 4000;
    function spin(flip) {
      var inner = flip.querySelector('.mt-flip__inner');
      if (!inner) return;
      var angle = (parseInt(flip.dataset.mtFlipAngle || '0', 10) || 0) + 180;
      flip.dataset.mtFlipAngle = String(angle);
      inner.style.transform = 'rotateY(' + angle + 'deg)';
      var onBack = (angle / 180) % 2 === 1;
      flip.classList.toggle('is-flipped', onBack);
      flip.setAttribute('aria-pressed', onBack ? 'true' : 'false');
      clearTimeout(flip._mtFlipTimer);
      if (onBack) {
        // Card backs are pretty but they don't sell — spin home after a beat.
        flip._mtFlipTimer = setTimeout(function () { spin(flip); }, AUTO_RETURN_MS);
      }
    }
    document.addEventListener('click', function (e) {
      var flip = e.target.closest ? e.target.closest('[data-mt-flip]') : null;
      if (flip) spin(flip);
    });
    document.addEventListener('keydown', function (e) {
      if (e.key !== 'Enter' && e.key !== ' ') return;
      var flip = e.target.closest ? e.target.closest('[data-mt-flip]') : null;
      if (!flip) return;
      e.preventDefault();
      spin(flip);
    });
  })();

  // ========================================================================
  // Pull Card Demo — shuffle, flip, edition switch
  // ========================================================================
  onReady(function () {
    document.querySelectorAll('[data-mt-pcd]').forEach(function (root) {
      var data = window.MT_DATA || {};
      var demoCards = data.demoCards || [];
      var mtImages  = data.cardImages || {};
      var gzImages  = data.gzCardImages || {};
      var mtBack    = data.mtCardBack || '';
      var gzBack    = data.gztCardBack || '';

      var state = 'ready'; // ready | shuffling | revealed
      var edition = 'mt';
      var currentCard = null;
      var pulled = new Set();
      var pullCount = 0;

      var tabs = root.querySelectorAll('[data-pcd-tab]');
      var stage = root.querySelector('.mt-pcd__stage');
      var flipper = root.querySelector('.mt-pcd__flipper');
      var backFace = root.querySelector('.mt-pcd__back');
      var backArt  = root.querySelector('.mt-pcd__back-art');
      var frontFace = root.querySelector('.mt-pcd__front');
      var readout = root.querySelector('.mt-pcd__readout');

      function setEdition(next) {
        if (next === edition) return;
        edition = next;
        currentCard = null;
        pulled = new Set();
        pullCount = 0;
        state = 'ready';
        tabs.forEach(function (t) {
          var isActive = t.getAttribute('data-pcd-tab') === edition;
          t.classList.toggle('is-active-mt', isActive && edition === 'mt');
          t.classList.toggle('is-active-gz', isActive && edition === 'gzt');
        });
        if (backArt) backArt.src = edition === 'gzt' ? gzBack : mtBack;
        if (flipper) flipper.classList.remove('is-revealed');
        renderReadout();
      }

      function shuffle() {
        if (state === 'shuffling') return;
        if (state === 'revealed') {
          if (flipper) flipper.classList.remove('is-revealed');
          state = 'ready';
          setTimeout(doShuffle, 500);
        } else {
          doShuffle();
        }
      }

      function doShuffle() {
        state = 'shuffling';
        if (backFace) backFace.classList.add('is-shuffling');
        renderReadout();
        setTimeout(function () {
          if (backFace) backFace.classList.remove('is-shuffling');
          var pool = edition === 'mt'
            ? demoCards.filter(function (c) { return !!mtImages[c.id]; })
            : demoCards.slice();
          if (pool.length === 0) pool = demoCards.slice();
          var avail = pool.filter(function (c) { return !pulled.has(c.id); });
          if (avail.length === 0) { pulled = new Set(); avail = pool; }
          var pick = avail[Math.floor(Math.random() * avail.length)];
          pulled.add(pick.id);
          currentCard = pick;
          pullCount += 1;
          state = 'revealed';
          renderFront();
          if (flipper) flipper.classList.add('is-revealed');
          renderReadout();
          // Engagement signal: visitor pulled a card in the demo. Fires
          // ONCE per session on first reveal — strongest engagement
          // moment on the page, makes a great retargeting audience seed.
          fireMetaOnce('pull_card_first_reveal', 'ViewContent', {
            content_name: 'pull_card_demo',
            content_category: 'engagement',
            content_ids: [pick.id]
          });
        }, 900);
      }

      function renderFront() {
        if (!frontFace || !currentCard) return;
        var isGZ = edition === 'gzt';
        var src = isGZ ? gzImages[currentCard.id] : mtImages[currentCard.id];
        if (src) {
          frontFace.innerHTML = '<img src="' + src + '" alt="' + escapeHtml(isGZ ? currentCard.gz : currentCard.mt) + '">';
        } else {
          var name = isGZ ? currentCard.gz : currentCard.mt;
          frontFace.innerHTML = '<div class="mt-pcd__front-stylized ' + (isGZ ? 'gz' : 'mt') + '">' +
            '<div class="mt-pcd__front-roman">' + escapeHtml(currentCard.roman) + '</div>' +
            '<div class="mt-pcd__front-name">' + escapeHtml(name) + '</div>' +
            '<div class="mt-pcd__front-trad">' + escapeHtml(currentCard.trad) + '</div>' +
          '</div>';
        }
      }

      function renderReadout() {
        if (!readout) return;
        if (state === 'ready') {
          readout.innerHTML =
            '<div class="mt-pcd__kicker">Try a reading</div>' +
            '<div class="mt-pcd__head">What does today have for you?</div>' +
            '<div class="mt-pcd__sub">Shuffle. Pull. The guidebook does the rest.</div>' +
            '<button class="mt-btn mt-btn--lime" data-pcd-shuffle>Shuffle &amp; pull</button>';
        } else if (state === 'shuffling') {
          readout.innerHTML =
            '<div class="mt-pcd__kicker">Shuffling</div>' +
            '<div class="mt-pcd__head"><span class="mt-pcd__dot">•</span><span class="mt-pcd__dot">•</span><span class="mt-pcd__dot">•</span></div>' +
            '<div class="mt-pcd__sub">Listening for what wants to come up.</div>';
        } else if (state === 'revealed' && currentCard) {
          var isGZ = edition === 'gzt';
          var name = isGZ ? currentCard.gz : currentCard.mt;
          var reading = isGZ ? currentCard.readingGz : currentCard.readingMt;
          readout.innerHTML =
            '<div class="mt-pcd__kicker">Card #' + pullCount + ' · Your ' + (isGZ ? 'Gen Z' : 'Millennial') + ' reading</div>' +
            '<div class="mt-pcd__head">' + escapeHtml(name) + '</div>' +
            '<div class="mt-pcd__body">"' + escapeHtml(reading) + '"</div>' +
            '<div class="mt-pcd__actions">' +
              '<button class="mt-btn mt-btn--ghost" data-pcd-shuffle>Pull another</button>' +
              '<a class="mt-pcd__secondary" data-pcd-jump-bundle>Get the real deck →</a>' +
            '</div>';
        }
      }

      // Event delegation within this root.
      // The stage element now carries `data-pcd-shuffle` (CA5), so any click
      // on the back/front face or aura area resolves through the same
      // `closest('[data-pcd-shuffle]')` check as the explicit Shuffle button.
      // shuffle() handles the state machine internally (returns in 'shuffling',
      // flips back from 'revealed', etc.) so no per-state guard is needed here.
      root.addEventListener('click', function (e) {
        if (e.target.matches('[data-pcd-tab]')) {
          setEdition(e.target.getAttribute('data-pcd-tab'));
        } else if (e.target.closest('[data-pcd-shuffle]')) {
          shuffle();
        } else if (e.target.closest('[data-pcd-jump-bundle]')) {
          var el = document.getElementById('mt-v2-bundle');
          if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
          markIntentfulNav('mt-v2-bundle');
        }
      });

      // Initial
      if (backArt) backArt.src = mtBack;
      renderReadout();
    });
  });

  // ========================================================================
  // Live Ticker — cycles every 10s, visible after scroll
  // ========================================================================
  onReady(function () {
    var el = document.querySelector('[data-mt-ticker]');
    if (!el) return;
    var data = window.MT_DATA || {};
    var notes = data.liveTickerNotes || [];
    if (notes.length === 0) return;
    var body = el.querySelector('.mt-ticker__body');
    var ago  = el.querySelector('.mt-ticker__ago');
    var idx = 0;

    function renderNote() {
      var n = notes[idx];
      if (body) body.innerHTML = '<strong>' + escapeHtml(n.who) + '</strong> in ' + escapeHtml(n.where) + ' just bought <strong>' + escapeHtml(n.what) + '</strong>.';
      if (ago)  ago.textContent  = n.ago + ' · ✓ Verified purchase';
    }

    var shown = false;
    function tick() {
      if (window.scrollY < 900) { el.classList.remove('is-visible'); return; }
      renderNote();
      el.classList.add('is-visible');
      shown = true;
      setTimeout(function () { el.classList.remove('is-visible'); }, 5500);
      setTimeout(function () { idx = (idx + 1) % notes.length; }, 6000);
    }

    setTimeout(tick, 3500);
    setInterval(tick, 10000);
  });

  // ========================================================================
  // Sticky ATC visibility — show after ~540px scroll
  // ========================================================================
  onReady(function () {
    var bar = document.querySelector('[data-mt-sticky]');
    if (!bar) return;
    var ticking = false;
    function onScroll() {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(function () {
        var visible = window.scrollY > 540;
        bar.classList.toggle('is-visible', visible);
        ticking = false;
      });
    }
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
  });

  // ========================================================================
  // Bundle builder — card selection (visual only; button carries variant id)
  // ========================================================================
  onReady(function () {
    var cards = document.querySelectorAll('[data-mt-bundle-card]');
    cards.forEach(function (card) {
      card.addEventListener('click', function (e) {
        // Don't hijack clicks on the actual ATC button
        if (e.target.closest('.btn-add-to-cart')) return;
        cards.forEach(function (c) { c.classList.remove('is-active'); });
        card.classList.add('is-active');
      });
    });
    // Default active state: the "duo" card
    var duo = document.querySelector('[data-mt-bundle-card="duo"]');
    if (duo) duo.classList.add('is-active');
  });

  // ========================================================================
  // About — expand/collapse long story
  // ========================================================================
  onReady(function () {
    var btn = document.querySelector('[data-mt-about-toggle]');
    var box = document.querySelector('[data-mt-about-long]');
    if (!btn || !box) return;
    btn.addEventListener('click', function () {
      var open = box.classList.toggle('is-open');
      btn.setAttribute('aria-expanded', open ? 'true' : 'false');
      var label = btn.querySelector('[data-label]');
      if (label) label.textContent = open ? 'Show less' : 'Read the full story';
      var chev = btn.querySelector('.mt-about__chev');
      if (chev) chev.style.transform = open ? 'rotate(180deg)' : 'rotate(0deg)';
    });
  });

  // ========================================================================
  // Nav — smooth scroll to sections
  //
  // Clarity's dead-click detector flags clicks that don't trigger a DOM
  // mutation or navigation within ~500ms — smooth-scroll does neither. Fire
  // a manual `clarity('event', 'in_page_jump')` so the click registers as
  // intentional and stops polluting the dead-click report (CA2).
  // ========================================================================
  function markIntentfulNav(targetId) {
    try {
      if (typeof window.clarity === 'function') {
        window.clarity('event', 'in_page_jump');
        if (targetId) window.clarity('set', 'last_jump', targetId);
      }
    } catch (e) {}
  }
  // 300ms ease-out scroll animation for in-page jumps. JS-driven instead of
  // native `behavior: 'smooth'` so the duration is predictable across browsers
  // and we can cancel an in-flight animation if the user clicks another jump.
  var currentScrollRaf = null;
  function easeOutCubic(t) { return 1 - Math.pow(1 - t, 3); }
  function animateScrollTo(targetY, duration, onComplete) {
    if (currentScrollRaf !== null) cancelAnimationFrame(currentScrollRaf);
    var startY = window.scrollY;
    var distance = targetY - startY;
    var startTime = performance.now();
    function step(now) {
      var elapsed = now - startTime;
      var progress = Math.min(elapsed / duration, 1);
      window.scrollTo(0, startY + distance * easeOutCubic(progress));
      if (progress < 1) {
        currentScrollRaf = requestAnimationFrame(step);
      } else {
        currentScrollRaf = null;
        if (onComplete) onComplete();
      }
    }
    currentScrollRaf = requestAnimationFrame(step);
  }
  function prefersReducedMotion() {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }
  // Shared section jump — used by every [data-mt-jump] element and by the
  // cart drawer's "Keep shopping" button (via window.mtJump), which is
  // re-rendered by JS and can't rely on the init-time listeners below.
  function jumpToSection(id) {
    var el = document.getElementById(id);
    // "Meet the maker" (mt-about-tries) replaces the journey section on the Millennial page (2026-10-05); its wrapper
    // carries the anchor so the footer's About link still lands.
    if (!el && id === 'mt-v2-about') el = document.getElementById('mt-v2-about-tries');
    if (el) {
          // Force-materialize all `content-visibility: auto` sections via the
          // `.mt-scrolling` class on <html> (CSS rule in mt-home.css ~1037),
          // then sync-flush layout. This pins every section's real height
          // BEFORE we capture the target Y. Without it, sections continue
          // materializing during the scroll and push the target downward —
          // the animation undershoots and the user lands in the preceding
          // section. We pay a brief layout cost (~10-40ms on mobile) once
          // per jump, then re-enable content-visibility skipping after the
          // animation completes.
          var html = document.documentElement;
          html.classList.add('mt-scrolling');
          void document.body.offsetHeight;

          var rect = el.getBoundingClientRect();
          var targetY = Math.max(0, window.scrollY + rect.top - 24); // -24 matches scroll-margin-top

          // NOTE: we deliberately do NOT remove `.mt-scrolling` on completion.
          // Removing it would re-skip distant content-visibility sections that
          // the user didn't scroll past, shrinking the document height. Mobile
          // Safari's scroll-anchoring doesn't reliably fire for
          // content-visibility height changes, so scrollY stays at its old
          // absolute Y while the target shifts UP in the document — leaving
          // the user past the target. Keeping the class permanent costs a
          // small one-time materialization on first jump, then nothing.
          if (prefersReducedMotion()) {
            window.scrollTo(0, targetY);
          } else {
            animateScrollTo(targetY, 300);
          }
        }
        markIntentfulNav(id);
  }
  window.mtJump = jumpToSection;
  onReady(function () {
    document.querySelectorAll('[data-mt-jump]').forEach(function (a) {
      a.addEventListener('click', function (e) {
        e.preventDefault();
        jumpToSection(a.getAttribute('data-mt-jump'));
      });
    });
  });

  // ========================================================================
  // Newsletter — Klaviyo client API submit to the brand's list. The account
  // key and list id come from window.MT_BRAND (snippets/mt-brand.liquid);
  // the fallbacks are Millennial's (list MT Email List: QY45E6).
  //
  // Klaviyo's client subscription endpoint requires:
  //   1. The company public key (`YtFp3e` for MT) in the URL.
  //   2. "Allow client-side subscriptions" enabled in Klaviyo Account → API Keys.
  //   3. A valid `revision` header.
  // 202 Accepted is the success response. Any 4xx/5xx is logged to console
  // and we surface a visible error instead of silently confirming.
  // ========================================================================
  onReady(function () {
    var form = document.querySelector('[data-mt-newsletter]');
    if (!form) return;
    var section = form.closest('.mt-nl');
    var brand = window.MT_BRAND || {};
    var klaviyoList = brand.klaviyoList || 'QY45E6';
    var klaviyoCompany = brand.klaviyoCompany || 'YtFp3e';
    var helpEmail = brand.key === 'gzt' ? brand.email : 'scott@millennialtarot.com';
    form.addEventListener('submit', function (e) {
      e.preventDefault();
      var input = form.querySelector('input[type="email"]');
      if (!input || !input.value || input.value.indexOf('@') === -1) return;
      var email = input.value;
      var body = {
        data: {
          type: 'subscription',
          attributes: {
            profile: { data: { type: 'profile', attributes: { email: email } } },
            custom_source: 'mt-home-v2-newsletter'
          },
          relationships: { list: { data: { type: 'list', id: klaviyoList } } }
        }
      };
      fetch('https://a.klaviyo.com/client/subscriptions/?company_id=' + klaviyoCompany, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', revision: '2024-10-15' },
        body: JSON.stringify(body)
      }).then(function (r) {
        if (r.status === 202) {
          console.log('[MT newsletter] subscribed:', email);
          if (section) {
            section.classList.add('is-sent');
            // The form disappears, so move focus to the thank-you for keyboard and screen reader users.
            var sent = section.querySelector('.mt-nl__sent');
            if (sent) { sent.setAttribute('tabindex', '-1'); sent.focus(); }
          }
          // Fire Meta `Lead` standard event ONLY on successful subscription
          // (not on click — accurate lead = real Klaviyo profile created).
          try {
            if (typeof window.fbq === 'function') {
              window.fbq('track', 'Lead', { content_name: 'newsletter_mt_home_v2', content_category: 'newsletter' });
            }
          } catch (e) {}
          // GA4 custom event via gtag direct (Shopify pixel doesn't auto-forward).
          try {
            if (typeof window.gtag === 'function') {
              window.gtag('event', 'newsletter_signup', { method: 'mt_home_v2', list_id: klaviyoList });
            }
          } catch (e) {}
        } else {
          return r.text().then(function (txt) {
            console.error('[MT newsletter] Klaviyo error', r.status, txt);
            alert('Something went wrong subscribing — try again or email ' + helpEmail + '.');
          });
        }
      }).catch(function (err) {
        console.error('[MT newsletter] network error', err);
        alert('Network error subscribing — try again or email ' + helpEmail + '.');
      });
    });
  });

  // ========================================================================
  // CART — full Shopify-wired drawer. Replaces the old cart-drawer.liquid.
  // UI lives in snippets/mt-cart-drawer.liquid; this module populates it by
  // fetching /cart.js and classifies line items as:
  //   - BUNDLE slot (duo variant)   — gets the "bundle row" treatment
  //   - LOOSE items (mt / gzt singles) — get individual rows
  //   - OTHER — rendered as a generic row (fallback for unknown variants)
  // ========================================================================
  (function mtCart() {
    // MT_VARIANTS is defined inside the cart-drawer snippet, which renders
    // AFTER content_for_layout — so this IIFE may initialize before it exists.
    // Read it lazily on every call instead of closing over a stale snapshot.
    var V = function() { return window.MT_VARIANTS || {}; };
    var drawer  = () => document.querySelector('[data-mt-cart]');
    var overlay = () => document.querySelector('[data-mt-cart-overlay]');
    var bodyEl  = () => document.querySelector('[data-mt-cart-body]');
    var footEl  = () => document.querySelector('[data-mt-cart-footer]');
    var benefit = () => document.querySelector('[data-mt-cart-benefit]');

    var DECK_IMG = {
      mt:  (window.MT_DATA && window.MT_DATA.assets && window.MT_DATA.assets.mtBox) || '',
      gzt: (window.MT_DATA && window.MT_DATA.assets && window.MT_DATA.assets.gzBox) || ''
    };
    var DECK_NAME = { mt: 'Millennial Tarot', gzt: 'Gen Z Tarot' };
    var DECK_SUB  = { mt: '78 cards + 132-page guidebook', gzt: '78 cards + 112-page guidebook' };

    function variantSlug(id) {
      id = parseInt(id, 10);
      var v = V();
      if (id === v.duo) return 'duo';
      if (id === v.mt)  return 'mt';
      if (id === v.gzt) return 'gzt';
      return null;
    }

    // Currency-aware formatter. Uses Shopify's `shop.money_format` template
    // (delivered via window.MT_CURRENCY.format) so cart prices respect the
    // active market. Supports {{amount}} and {{amount_no_decimals}} placeholders.
    function usd(cents) {
      var amount = (cents / 100).toFixed(2);
      var fmt = (window.MT_CURRENCY && window.MT_CURRENCY.format) || '${{amount}}';
      return fmt
        .replace(/\{\{\s*amount\s*\}\}/, amount)
        .replace(/\{\{\s*amount_no_decimals\s*\}\}/, Math.round(cents / 100))
        .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/, amount.replace('.', ','));
    }

    // Partition Shopify cart items into our domain model.
    function classify(items) {
      var bundle = null, loose = [], other = [];
      items.forEach(function (it) {
        var slug = variantSlug(it.variant_id);
        if (slug === 'duo') bundle = it;
        else if (slug === 'mt' || slug === 'gzt') loose.push(Object.assign({ slug: slug }, it));
        else other.push(it);
      });
      return { bundle: bundle, loose: loose, other: other };
    }

    function esc(s) {
      return String(s == null ? '' : s)
        .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
    }

    function renderItemRow(it, opts) {
      opts = opts || {};
      var slug = it.slug;
      var isBundle = !slug;
      var name     = isBundle ? it.product_title : DECK_NAME[slug];
      // The brand's own deck is named first and sits in front (Scott, 2026-10-02: on the Gen Z store, "gen z first and then Millennial and for the image to be that way too")
      var lead = (window.MT_BRAND && window.MT_BRAND.leadDeck) === 'gzt' ? 'gzt' : 'mt';
      var subtitle = isBundle ? (lead === 'gzt' ? 'Gen Z Tarot + Millennial Tarot' : 'Millennial Tarot + Gen Z Tarot') : DECK_SUB[slug];
      var img = isBundle
        ? '<div class="mt-cart__thumb-bundle">' +
            (lead === 'gzt'
              ? '<img src="' + esc(DECK_IMG.gzt) + '" alt="Gen Z Tarot" style="transform:rotate(-6deg); position:relative; z-index:1">' +
                '<img src="' + esc(DECK_IMG.mt)  + '" alt="Millennial Tarot" style="transform:rotate(6deg); margin-left:-24px">'
              : '<img src="' + esc(DECK_IMG.mt)  + '" alt="Millennial Tarot" style="transform:rotate(-6deg)">' +
                '<img src="' + esc(DECK_IMG.gzt) + '" alt="Gen Z Tarot" style="transform:rotate(6deg); margin-left:-24px; z-index:1">') +
          '</div>'
        : '<div class="mt-cart__thumb-solo">' +
            '<img src="' + esc(DECK_IMG[slug]) + '" alt="' + esc(name) + '">' +
          '</div>';

      var linePrice = it.final_line_price; // cents, already includes discounts
      var compare   = (it.original_line_price && it.original_line_price > linePrice)
        ? '<div class="mt-cart__row-compare">' + usd(it.original_line_price) + '</div>' : '';

      // Inline ship-nudge — clickable button between subtitle and qty controls.
      // Reuses [data-mt-cart-qty-inc] so existing click delegation handles it
      // (effectively "+1 to qty", which clears the nudge once qty hits 2).
      var shipNudge = opts.shipNudge
        ? '<button type="button" class="mt-cart__row-ship-nudge" data-mt-cart-qty-inc>' +
            '<span class="mt-cart__row-ship-nudge-icon">+</span>' +
            'Add another to ship free' +
          '</button>'
        : '';

      return (
        '<div class="mt-cart__row" data-key="' + esc(it.key) + '" data-variant-id="' + esc(it.variant_id) + '">' +
          img +
          '<div style="flex:1">' +
            '<div class="mt-cart__row-title">' + esc(name) + '</div>' +
            '<div class="mt-cart__row-sub">' + esc(subtitle) + '</div>' +
            shipNudge +
            '<div class="mt-cart__row-qty">' +
              '<button class="mt-cart__qty-btn" data-mt-cart-qty-dec>\u2212</button>' +
              '<span class="mt-cart__qty-val">' + it.quantity + '</span>' +
              '<button class="mt-cart__qty-btn" data-mt-cart-qty-inc>+</button>' +
              '<a class="mt-cart__remove" data-mt-cart-remove>Remove</a>' +
            '</div>' +
          '</div>' +
          '<div class="mt-cart__row-price">' + usd(linePrice) + compare + '</div>' +
        '</div>'
      );
    }

    function renderUpsell(parts, currentShippingCents) {
      // One-tap single → duo upgrade strip. Shows only when the cart is
      // exactly one loose single (no duo already, no unknown items) —
      // tapping [data-mt-cart-promote] runs promote(), which swaps the
      // single line for the duo bundle. One line + one button, on purpose:
      // the earlier multi-line upsell card tested as confusing.
      if (parts.bundle || parts.other.length > 0 || parts.loose.length !== 1) return '';
      var only = parts.loose[0];
      var otherSlug = only.slug === 'mt' ? 'gzt' : 'mt';
      var otherDeck = DECK_NAME[otherSlug];
      var data = window.MT_DATA || {};
      // Fallback: $54.99 (reverted 2026-09-13 after a 10-day $59.99 test). It only fires if
      // MT_DATA is missing, but a stale fallback quietly understates the
      // upgrade cost, which is the worst direction to be wrong in.
      var duoCents = Math.round((((data.bundles || {}).duo || {}).price || 54.99) * 100);
      // Honest incremental cost = duo total minus what the single already
      // costs them INCLUDING the shipping the bundle makes free (US only).
      // At the old $54.99/$29.99/$3.99 that was ~$21 rather than the $25
      // sticker delta. Free shipping is currently on for everything, so the
      // ship credit is 0 and the two collapse to the same number ($25 today).
      // freeShippingTest: nothing charges shipping, so the incremental cost
      // is the plain sticker delta (bundle − single) with no ship framing.
      var shipCredit = (!freeShipTest() && isUSMarket()) ? currentShippingCents : 0;
      var upgradeCents = duoCents - only.final_price - shipCredit;
      if (upgradeCents <= 0) return '';
      // New offer frame: struck solo price + "~N% off" — the % computed
      // from live prices, never hardcoded (which is why this line survived
      // the 2026-09-03 reprice untouched while the announcement bar's
      // hardcoded "33%" became a false claim). wasCents = the other deck's
      // solo price; pct = how much cheaper the upgrade is vs buying it solo.
      var wasCents = Math.round((((data.products || {})[otherSlug] || {}).price || 0) * 100);
      var pct = wasCents > upgradeCents ? Math.round((wasCents - upgradeCents) * 100 / wasCents) : 0;
      // Struck anchor + the actual price they'd pay + the discount:
      // "$32.99-struck $22 (~33% off)" at today's prices. The price tag matters — a bare
      // "% off" left the second deck priceless (Scott, Jul 3).
      var exact = upgradeCents % 100 === 0;
      var payLabel = isUSMarket()
        ? (exact ? '$' : '~$') + Math.round(upgradeCents / 100)
        : usd(upgradeCents);
      var dealLabel;
      if (pct > 0) {
        dealLabel = '<span class="mt-cart__upsell-was">' + usd(wasCents) + '</span> <strong>' + payLabel + '</strong> (~' + pct + '% off)';
      } else {
        // Fallback if prices ever make the pct meaningless: plain upgrade cost.
        dealLabel = '<strong>' + payLabel + '</strong>';
      }
      // Legacy (knob off): still note the shipping unlock when it's real.
      var shipLine = (!freeShipTest() && isUSMarket() && currentShippingCents > 0) ? ' — and shipping’s free.' : '.';
      return (
        '<div class="mt-cart__upsell" data-mt-cart-upsell data-loose-key="' + esc(only.key) + '" data-loose-qty="' + only.quantity + '">' +
          '<img class="mt-cart__upsell-img" src="' + esc(DECK_IMG[otherSlug]) + '" alt="' + esc(otherDeck) + '">' +
          '<div class="mt-cart__upsell-line">✦ Add <strong>' + esc(otherDeck) + '</strong> — ' + dealLabel + shipLine + '</div>' +
          '<button class="mt-btn mt-btn--lime mt-cart__upsell-btn" data-mt-cart-promote>Add</button>' +
        '</div>'
      );
    }

    // "Great choice." and three reasons under the item as a soft note
    // (Scott, 2026-10-06, on a friend's advice that the cart should affirm the add; the note goes after the item,
    // styled as a message, not a control). The reasons live on the drawer element, written by
    // mt-cart-drawer.liquid, so the words are Liquid's and the Gen Z store can give its own. Named for the newest
    // item: the bundle by its product title, a single deck by its deck name. part = 'title' | 'note'.
    function renderAffirm(cart, part) {
      var d = drawer();
      if (!d || d.getAttribute('data-mt-cart-affirm') !== 'true') return '';
      var newest = (cart.items || [])[0];
      if (!newest) return '';
      var slug = variantSlug(newest.variant_id);
      var attr = slug === 'mt' || slug === 'gzt' ? 'data-mt-cart-affirm-lines-' + slug : 'data-mt-cart-affirm-lines';
      var lines = (d.getAttribute(attr) || '').split('|').map(function (s) { return s.trim(); }).filter(Boolean);
      if (!lines.length) return '';
      // One card under the item: "Great choice." and the reasons together (Scott, 2026-10-22: the line above the
      // rows and the bullets below it "feel quite disjointed... it can be shorter and more to the point").
      return (
        '<div class="mt-cart__affirm-note" role="note">' +
          '<div class="mt-cart__affirm-head">Great choice.</div>' +
          '<ul class="mt-cart__affirm-list">' + lines.map(function (s) { return '<li>' + esc(s) + '</li>'; }).join('') + '</ul>' +
        '</div>'
      );
    }

    // Free-shipping threshold (US, in cents). Mirrors the Shopify shipping rule
    // "free shipping over $50 in the US" — so two singles ($59.98) ship free
    // even if they're both the same deck (no bundle discount, but free ship).
    var FREE_SHIP_THRESHOLD_CENTS = 5000;
    // freeShippingTest — set by the mirrored knob in mt-cart-drawer.liquid
    // (see the knob at the top of templates/index.liquid). When on, every
    // order ships free: no $3.99 line, no ship-unlock framing in the strip.
    function freeShipTest() {
      var d = drawer();
      return !!(d && d.getAttribute('data-mt-free-ship-test') === 'true');
    }
    function isUSMarket() {
      return !window.MT_CURRENCY || window.MT_CURRENCY.code === 'USD';
    }
    // Format a savings amount with the same rounding logic the rest of the page
    // uses: in USD round UP to the next whole dollar ($5, not $4.99); elsewhere
    // pass through the Shopify-formatted currency string. Returns "" if the
    // savings is non-positive (compare-at didn't convert in this market).
    function formatSavings(saveCents) {
      if (saveCents <= 0) return '';
      if (isUSMarket()) return '$' + Math.ceil(saveCents / 100);
      return usd(saveCents);
    }

    function renderBenefit(bundle, loose, subtotalCents) {
      var el = benefit();
      if (!el) return;
      var hasBundle = !!bundle;
      var hasLoose  = loose.length > 0;
      if (!hasBundle && !hasLoose) { el.hidden = true; el.innerHTML = ''; return; }
      el.hidden = false;
      // Pull the savings amount from MT_DATA so it matches the rest of the page
      // and respects the active currency (Shopify's money_format via usd()).
      var data = window.MT_DATA || {};
      var compare = (data.bundles && data.bundles.duo && data.bundles.duo.compare) || 0;
      var price   = (data.bundles && data.bundles.duo && data.bundles.duo.price)   || 0;
      var saveCents = Math.round((compare - price) * 100);
      var saveLabel = formatSavings(saveCents);
      var meetsFreeShip = isUSMarket() && subtotalCents >= FREE_SHIP_THRESHOLD_CENTS;
      if (hasBundle) {
        el.className = 'mt-cart__benefit mt-cart__benefit--on';
        el.innerHTML = '<span class="mt-cart__benefit-icon">✦</span><span>Bundle unlocked — <strong>free shipping' + (saveLabel ? ' + ' + saveLabel + ' off' : '') + '</strong>.</span>';
      } else if (meetsFreeShip) {
        // Two-or-more singles already past the $50 free-ship threshold.
        // Frame remaining upside as "add the OTHER deck to also save $X".
        el.className = 'mt-cart__benefit mt-cart__benefit--on';
        el.innerHTML = '<span class="mt-cart__benefit-icon">✦</span><span>Free U.S. shipping unlocked' + (saveLabel ? ' — add the other deck to <strong>save ' + saveLabel + '</strong> on top' : '') + '.</span>';
      } else {
        el.className = 'mt-cart__benefit mt-cart__benefit--off';
        el.innerHTML = '<span class="mt-cart__benefit-icon">○</span><span>Add the other deck to <strong>' + (saveLabel ? 'save ' + saveLabel + ' and ' : '') + 'ship free</strong>.</span>';
      }
    }

    // Whether to show the inline "add another to ship free" nudge inside a
    // loose-deck row. True only when:
    //   - we're in the US (where the $50 free-ship rule applies),
    //   - exactly one loose row exists with quantity 1,
    //   - subtotal is below the threshold (still solvable by qty bump).
    // Bumping the qty on that row to 2 clears the nudge automatically.
    function shouldShowShipNudge(loose, it, subtotalCents) {
      if (!isUSMarket()) return false;
      if (loose.length !== 1) return false;
      if (it.quantity !== 1) return false;
      if (subtotalCents >= FREE_SHIP_THRESHOLD_CENTS) return false;
      return true;
    }

    function render(cart) {
      // Always refresh the nav cart badge — every cart mutation routes through render().
      updateBadges(cart);

      var body = bodyEl();
      var foot = footEl();
      if (!body || !foot) return;

      var parts = classify(cart.items || []);
      var subtotal = cart.items_subtotal_price || cart.total_price || 0;
      renderBenefit(parts.bundle, parts.loose, subtotal);

      if (!parts.bundle && parts.loose.length === 0 && parts.other.length === 0) {
        body.innerHTML =
          '<div class="mt-cart__empty">' +
            '<div class="mt-cart__empty-icon">✦</div>' +
            '<div class="mt-cart__empty-title">Your cart is empty.</div>' +
            '<div class="mt-cart__empty-sub">The deck doesn\u2019t shuffle itself.</div>' +
            '<button class="mt-btn mt-btn--lime" data-mt-cart-close data-mt-cart-jump="mt-v2-bundle">Keep shopping</button>' +
          '</div>';
        foot.hidden = true;
        return;
      }

      // Shipping — computed before the rows so the upsell strip can quote
      // the honest ship-inclusive upgrade cost. Free U.S. shipping fires when:
      //   - the duo bundle is in cart (Shopify-side rule), OR
      //   - subtotal ≥ $50 (Shopify shipping profile rule "free over $50 US")
      // For non-US markets we don't fake a flat rate — the cart drawer prints
      // "at checkout" and Shopify computes the real cost.
      var shipFree = freeShipTest() || !!parts.bundle || (isUSMarket() && subtotal >= FREE_SHIP_THRESHOLD_CENTS);
      var shipping = shipFree ? 0 : ((parts.loose.length > 0 || parts.other.length > 0) ? 399 : 0);
      var total    = subtotal + shipping;

      // Render items oldest-first / newest-at-the-bottom. Shopify's /cart.js
      // returns items in reverse chronological order by default (newest at
      // index 0), so we .slice().reverse() to flip it. This matches the
      // mental model "the thing I just added is at the bottom" and lines
      // the real row up with the skeleton row that addVariant() appends at
      // the body's end — skeleton -> real row, same position, no jump.
      var rows = [];
      (cart.items || []).slice().reverse().forEach(function (it) {
        var slug = variantSlug(it.variant_id);
        if (slug === 'duo') {
          // Bundle row — renderItemRow's isBundle path expects no slug field
          rows.push(renderItemRow(it));
        } else if (slug === 'mt' || slug === 'gzt') {
          var tagged = Object.assign({ slug: slug }, it);
          rows.push(renderItemRow(tagged, {
            shipNudge: shouldShowShipNudge(parts.loose, tagged, subtotal)
          }));
        } else {
          rows.push(renderItemRow(it));
        }
      });
      // The other-deck offer sits right under the item so a one-deck cart sees it (Scott, 2026-10-22); the note follows.
      rows.push(renderUpsell(parts, shipping));
      rows.push(renderAffirm(cart, 'note'));
      body.innerHTML = rows.join('');

      // Footer totals. Shopify cart.total_price includes any automatic discounts.
      foot.hidden = false;
      foot.querySelector('[data-mt-cart-subtotal]').textContent = usd(subtotal);
      foot.querySelector('[data-mt-cart-shipping]').innerHTML =
        shipFree ? '<strong style="color:var(--mt-accent-text)">FREE</strong>' : usd(shipping);
      // shipping cost above is a placeholder — Shopify computes the real rate
      // at checkout. For non-US visitors with no flat rate, leave it blank
      // rather than printing a fake "$3.99" in the wrong currency.
      if (!shipFree && (window.MT_CURRENCY && window.MT_CURRENCY.code !== 'USD')) {
        foot.querySelector('[data-mt-cart-shipping]').textContent = 'at checkout';
      }
      foot.querySelector('[data-mt-cart-total]').textContent = usd(total);
      // Checkout button carries no price — the Total row sits directly above it.
    }

    async function fetchCart() {
      try {
        var r = await fetch('/cart.js', { headers: { 'Accept': 'application/json' }});
        var cart = await r.json();
        render(cart);
        return cart;
      } catch (e) { console.error('[MT cart] fetch failed', e); }
    }

    // Cart open/close — body overflow toggled via class instead of inline
    // style so the browser can batch the change with the drawer's existing
    // class transitions. Inline-style mutations force a separate style recalc;
    // a class toggle hits CSS rules cached at parse time. (CA6 INP win.)
    // Kept synchronous — rAF wrappers paused in backgrounded tabs caused the
    // drawer to never open if the user clicked while the tab wasn't focused.
    function open() {
      var d = drawer(), o = overlay();
      if (d) { d.classList.add('is-open'); d.setAttribute('aria-hidden', 'false'); }
      if (o) { o.classList.add('is-open'); }
      document.body.classList.add('mt-cart-open');
    }
    function close() {
      var d = drawer(), o = overlay();
      if (d) { d.classList.remove('is-open'); d.setAttribute('aria-hidden', 'true'); }
      if (o) { o.classList.remove('is-open'); }
      document.body.classList.remove('mt-cart-open');
    }

    async function addVariant(variantId, qty) {
      // Open the drawer FIRST so the slide-out animation runs in parallel
      // with the network round-trip. Before, open() ran AFTER both fetches
      // serially resolved (~600-1000ms wait). Now the user sees the drawer
      // immediately — render() updates it once /cart.js returns.
      // /cart/add.js's response body is the added line item, not the full
      // cart, so we still need fetchCart() to populate render().
      //
      // Mask the brief gap with a loading state — otherwise the hardcoded
      // "Your cart is empty" Liquid body flashes for ~300ms on cold-start
      // adds and reads as broken. The loading state is overwritten the
      // moment render() runs.
      // Three pre-fetch UX states based on what the click will actually do:
      //
      //  (1) Variant is already in cart — Shopify will bump qty on the
      //      existing line, no new row appears. Skip the skeleton (it'd
      //      represent a phantom new row) and instead pulse the existing
      //      row's qty number so the user gets feedback. render() replaces
      //      the body when /cart.js resolves, which clears the pulse class
      //      automatically.
      //
      //  (2) Cart is empty — replace the empty-state markup with a single
      //      skeleton row. Render() will swap it for the real row.
      //
      //  (3) Cart has different items — append a skeleton row at the bottom
      //      (before the upsell card if one is rendered). Since render() now
      //      iterates cart.items in Shopify's insertion order, the real row
      //      lands at this same bottom position when /cart.js resolves —
      //      skeleton -> real row, no visual jump.
      var body = bodyEl();
      var foot = footEl();
      var existingRow = body
        ? body.querySelector('.mt-cart__row[data-variant-id="' + variantId + '"]')
        : null;
      var skelHTML =
        '<div class="mt-cart__row mt-cart__row--skeleton" aria-label="Adding to cart">' +
          '<div class="mt-cart__skel-thumb"></div>' +
          '<div style="flex:1">' +
            '<div class="mt-cart__skel-label">Adding to cart' +
              '<span class="mt-cart__loading-dots"><span>.</span><span>.</span><span>.</span></span>' +
            '</div>' +
            '<div class="mt-cart__skel-bar mt-cart__skel-bar--sub"></div>' +
            '<div class="mt-cart__skel-bar mt-cart__skel-bar--qty"></div>' +
          '</div>' +
          '<div class="mt-cart__skel-bar mt-cart__skel-bar--price"></div>' +
        '</div>';
      if (existingRow) {
        existingRow.classList.add('mt-cart__row--updating');
      } else if (body) {
        var hasOtherRows = body.querySelector('.mt-cart__row');
        if (hasOtherRows) {
          var upsell = body.querySelector('.mt-cart__upsell');
          if (upsell) upsell.insertAdjacentHTML('beforebegin', skelHTML);
          else body.insertAdjacentHTML('beforeend', skelHTML);
        } else {
          body.innerHTML = skelHTML;
          if (foot) foot.hidden = true;
        }
      }
      open();
      try {
        var r = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ items: [{ id: parseInt(variantId, 10), quantity: qty || 1 }] })
        });
        var data = await r.json();
        if (!r.ok) {
          console.error('[MT cart] add failed', data);
          alert(data.description || data.message || 'Unable to add to cart');
          // Close the drawer so the user isn't left staring at a state
          // that doesn't reflect what actually happened.
          close();
          return;
        }
        // Stamp the cart with the A/B assignment + ad, so the variant this
        // buyer actually saw rides onto the order as note_attributes. Shopify
        // is the only system that sees every order, so this is the real
        // variant→purchase join (see syncCartAttributes in mt-ab.js).
        // Deliberately NOT awaited — a tracking write must never delay the
        // drawer or the buy-now redirect. It uses keepalive to survive the
        // navigation.
        if (window.MT_AB && typeof window.MT_AB.syncCartAttributes === 'function') {
          try { window.MT_AB.syncCartAttributes(); } catch (e) {}
        }
        await fetchCart();
      } catch (e) {
        console.error('[MT cart] add error', e);
        close();
      }
    }

    async function changeKey(key, quantity) {
      try {
        await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: key, quantity: quantity })
        });
        await fetchCart();
      } catch (e) { console.error('[MT cart] change error', e); }
    }

    // Promote one of the loose deck units into a duo bundle.
    // - If qty was 2+: decrement by 1, keep the extras as singles.
    //   (e.g. 3× MT + click promote → 2× MT + 1× duo bundle)
    // - If qty was 1: remove the loose row entirely.
    // Either way, +1 duo bundle gets added.
    //
    // ORDER MATTERS: add the duo FIRST, remove the single second. The old
    // remove-then-add order emptied the whole cart when Shopify throttled
    // the second mutation (back-to-back cart POSTs can 429/503) — the
    // single was already gone and the duo never arrived.
    async function promote(looseKey, looseQty) {
      var v = V();
      if (!v.duo) return;
      var nextQty = Math.max(0, (parseInt(looseQty, 10) || 1) - 1);
      try {
        var addRes = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ items: [{ id: v.duo, quantity: 1 }] })
        });
        if (!addRes.ok) {
          // Cart untouched — re-render so the strip's loading button resets.
          console.error('[MT cart] promote add failed', addRes.status);
          await fetchCart();
          return;
        }
        // Drop one unit of the loose single, with one retry for throttling.
        // If both attempts fail the cart holds duo + single — recoverable in
        // the drawer, never empty.
        var changeInit = {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: looseKey, quantity: nextQty })
        };
        var chRes = await fetch('/cart/change.js', changeInit);
        if (!chRes.ok) {
          await new Promise(function (resolve) { setTimeout(resolve, 700); });
          await fetch('/cart/change.js', changeInit);
        }
        await fetchCart();
      } catch (e) {
        console.error('[MT cart] promote error', e);
        await fetchCart();
      }
    }

    // Global click delegation (replaces the old cart-drawer.liquid listeners)
    document.addEventListener('click', function (e) {
      var t = e.target;

      // Add-to-cart button. Clarity showed double-clicks on ATC because the
      // button gave no feedback while /cart/add.js was in flight — so put it
      // in a real loading state (spinner + dimmed, `.mt-btn.loading` CSS) and
      // ignore repeat clicks until the add resolves. The drawer still opens
      // instantly inside addVariant().
      var atc = t.closest ? t.closest('.btn-add-to-cart') : null;
      if (atc) {
        e.preventDefault();
        if (atc.classList.contains('loading')) return;
        var id = atc.getAttribute('data-variant-id');
        if (id) {
          atc.classList.add('loading');
          atc.disabled = true;
          addVariant(id, 1).finally(function () {
            atc.classList.remove('loading');
            atc.disabled = false;
          });
        }
        return;
      }

      // Buy now → add + go to /checkout
      var buy = t.closest ? t.closest('.btn-buy-now') : null;
      if (buy) {
        e.preventDefault();
        var bid = buy.getAttribute('data-variant-id');
        if (bid) {
          (async () => {
            await addVariant(bid, 1);
            window.location.href = '/checkout';
          })();
        }
        return;
      }

      // Header cart icon
      var open_ = t.closest ? t.closest('.w-commerce-commercecartopenlink, .cart-button, [data-mt-cart-open]') : null;
      if (open_) {
        e.preventDefault();
        e.stopPropagation();
        open();
        return;
      }

      // Close cart (overlay or X). A closer can also carry
      // data-mt-cart-jump="<sectionId>" — the "Keep shopping" button uses it
      // to land the visitor on the decks/buy-box section instead of wherever
      // the drawer happened to open. Delegated (not init-bound) because the
      // empty-state button is re-rendered by render().
      if (t.matches && (t.matches('[data-mt-cart-close]') || t.matches('[data-mt-cart-overlay]'))) {
        e.preventDefault();
        close();
        var jumpTarget = t.getAttribute && t.getAttribute('data-mt-cart-jump');
        if (jumpTarget && window.mtJump) window.mtJump(jumpTarget);
        return;
      }

      // Quantity + / -
      var row = t.closest ? t.closest('.mt-cart__row[data-key]') : null;
      if (row) {
        var key = row.getAttribute('data-key');
        var qtyEl = row.querySelector('.mt-cart__qty-val');
        var cur = parseInt(qtyEl ? qtyEl.textContent : '1', 10) || 1;
        if (t.matches('[data-mt-cart-qty-dec]')) { e.preventDefault(); changeKey(key, Math.max(0, cur - 1)); return; }
        if (t.matches('[data-mt-cart-qty-inc]')) { e.preventDefault(); changeKey(key, cur + 1); return; }
        if (t.matches('[data-mt-cart-remove]'))  { e.preventDefault(); changeKey(key, 0); return; }
      }

      // Promote-to-bundle upsell — pass current qty so promote() can decrement
      // by 1 instead of zeroing the line (preserves extras when qty > 1).
      if (t.matches && t.matches('[data-mt-cart-promote]')) {
        e.preventDefault();
        if (t.classList.contains('loading')) return;
        var up = t.closest('[data-mt-cart-upsell]');
        var looseKey = up ? up.getAttribute('data-loose-key') : null;
        var looseQty = up ? up.getAttribute('data-loose-qty') : '1';
        if (looseKey) {
          // Counted in GA4 so the cart offer's take rate can be read (2026-10-22: before this, only Clarity's click text showed it)
          try { if (typeof window.gtag === 'function') window.gtag('event', 'mt_cart_upsell_click', { offer: 'other_deck' }); } catch (err) {}
          // Loading feedback until the swap resolves — render() replaces the
          // whole strip afterward, so no need to clear the class.
          t.classList.add('loading');
          t.disabled = true;
          promote(looseKey, looseQty);
        }
        return;
      }
    });

    // Escape to close
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && drawer() && drawer().classList.contains('is-open')) close();
    });

    // Populate badges in the nav
    function updateBadges(cart) {
      var n = cart.item_count || 0;
      document.querySelectorAll('.mt-nav__cart-badge, .cart-count').forEach(function (b) {
        b.textContent = n;
        b.style.display = n > 0 ? '' : 'none';
      });
    }

    // Initial load — run as soon as DOM is ready. Was wrapped in
    // requestIdleCallback({ timeout: 2000 }) for INP smoothness, but that
    // delayed the cart fetch by up to 2s, which meant returning visitors
    // with items in cart saw the hardcoded "Your cart is empty" Liquid
    // body for ~2-5s after page load (until idle + /cart.js round-trip
    // completed). Trading a tiny bit of first-paint headroom for the
    // drawer being accurate from second 0.
    function deferInit() {
      fetchCart().then(function (cart) { if (cart) updateBadges(cart); });
    }
    onReady(deferInit);

    // Expose for external scripts if needed
    window.mtCart = {
      open: open, close: close, refresh: fetchCart,
      add: addVariant, promote: promote
    };
  })();

  // ========================================================================
  // Declarative analytics tagging via `data-meta-event`.
  //
  // NAMING NOTE: The attribute is named `data-meta-event` for historical
  // reasons — it originally fired only to Meta Pixel. As of 2026-05-13 it
  // routes to BOTH Meta Pixel AND GA4, with one rule:
  //
  //   ViewContent events → GA4 ONLY (skipped at Meta — see META RULE
  //                       comment above fireMetaOnce). All theme-fired
  //                       ViewContents are engagement (button clicks,
  //                       nav jumps, FAQ expand, cart open). Real
  //                       ViewContent fires from Shopify auto-Pixel on
  //                       product page loads.
  //
  //   All other event names → Meta Pixel + GA4. Use these for actions
  //                           Shopify Customer Events does NOT already
  //                           cover (cart/checkout/purchase are auto —
  //                           adding the attribute there would double-fire).
  //
  // Don't rename the attribute — too many snippets reference it (~30+
  // buttons across nav/footer/hero/faq/cart-drawer). Future tag: think
  // of it as "data-tracking-event" routed by event_name.
  //
  // Standard event names this handler is designed to receive:
  //   Lead         — newsletter signup, demo request    → Meta + GA4
  //   ViewContent  — engagement (nav, FAQ, cart open)   → GA4 only
  //   StartTrial   — quiz start, free reading start     → Meta + GA4
  //   Contact      — email link click, social outbound  → Meta + GA4
  //   Search       — site search submit                 → Meta + GA4
  //
  // Optional attrs:
  //   data-meta-event-label    → fbq's `content_name` + gtag's `event_label`
  //   data-meta-event-value    → numeric value (e.g., "29.99")
  //   data-meta-event-category → fbq's `content_category`
  //
  // Example (engagement — GA4 only):
  //   <button data-meta-event="ViewContent" data-meta-event-label="faq_expand"
  //           data-meta-event-category="engagement">FAQ Q →</button>
  //
  // Example (real Meta event — both Meta + GA4):
  //   <a data-meta-event="Contact" data-meta-event-label="contact_email"
  //      href="mailto:hi@millennialtarot.com">Contact</a>
  // ========================================================================
  document.addEventListener('click', function (e) {
    // composedPath reaches inside shadow roots (the reviews section renders in one), where e.target is only the host.
    var el = null, path = e.composedPath ? e.composedPath() : [];
    for (var i = 0; i < path.length; i++) { if (path[i].getAttribute && path[i].hasAttribute('data-meta-event')) { el = path[i]; break; } }
    if (!el) el = e.target.closest('[data-meta-event]');
    if (!el) return;
    var name  = el.getAttribute('data-meta-event');
    var label = el.getAttribute('data-meta-event-label') || '';
    var cat   = el.getAttribute('data-meta-event-category') || '';
    var valueRaw = el.getAttribute('data-meta-event-value');
    var value = valueRaw ? parseFloat(valueRaw) : null;
    if (!name) return;

    // Defer the actual fbq/gtag calls to a microtask so the click handler
    // returns to the event loop immediately — keeps INP low (CA6).
    var fire = function () {
      // Meta pixel — but skip ViewContent (see META RULE above fireMetaOnce).
      // ViewContent from theme buttons is always engagement (nav jumps, FAQ
      // expands, cart open, footer links). The real ViewContent comes from
      // Shopify's auto-Pixel on product page loads. Sending button-click
      // ViewContents inflates Pixel count vs. CAPI and degrades coverage.
      try {
        if (typeof window.fbq === 'function' && name !== 'ViewContent') {
          var payload = {};
          if (label) payload.content_name = label;
          if (cat)   payload.content_category = cat;
          if (value !== null && !isNaN(value)) { payload.value = value; payload.currency = 'USD'; }
          window.fbq('track', name, payload);
        }
      } catch (err) { console.error('[mt-meta-event] fbq failed', err); }

      // GA4 — lowercase snake_case event name with mt_ prefix to keep custom
      // events distinct from Shopify-fired standard events.
      try {
        if (typeof window.gtag === 'function') {
          var gaName = 'mt_' + String(name).replace(/([a-z])([A-Z])/g, '$1_$2').toLowerCase();
          var gaPayload = {};
          if (label) gaPayload.event_label = label;
          if (cat)   gaPayload.event_category = cat;
          if (value !== null && !isNaN(value)) gaPayload.value = value;
          window.gtag('event', gaName, gaPayload);
        }
      } catch (err) { console.error('[mt-meta-event] gtag failed', err); }

      // Clarity — an event for counting, a tag for FILTERING. The tag is what
      // makes "show me sessions that used the sticky bar" possible in the
      // Clarity UI; without it every CTA collapses into one ClickedText
      // string and the surfaces are indistinguishable. Category is the tag
      // key on purpose so the key set stays small (cta / navigation / cart).
      // Mirrors the in_page_jump pattern above.
      try {
        if (typeof window.clarity === 'function' && label) {
          window.clarity('event', 'mt_' + label);
          if (cat) window.clarity('set', cat, label);
        }
      } catch (err) { /* analytics must never break the click */ }
    };
    // Microtask defer — runs after the click handler returns but before paint.
    // Browsers without queueMicrotask (none current) fall back to Promise.then.
    if (typeof queueMicrotask === 'function') queueMicrotask(fire);
    else Promise.resolve().then(fire);
  }, { capture: true });

  // ========================================================================
  // Hero CARDS variant — floating wreath cycle
  //
  // 5 cards orbit at fixed slots; every 5s a back card swaps with the front.
  // Click anywhere on the wrap advances immediately and resets the timer.
  // Slot positions, queue order, and the 0.8s z-index transition delay all
  // match the Cloud Design React preview (HeroRightCards). The delayed
  // z-index step is what kills the "front flash" mid-flight when two cards
  // visually cross each other.
  // ========================================================================
  onReady(function () {
    document.querySelectorAll('[data-mt-float-cards]').forEach(function (root) {
      var cards = Array.prototype.slice.call(root.querySelectorAll('.mt-hero__float-card[data-card-idx]'));
      if (cards.length < 5) return;

      var slots = [
        { x: 52, y: 48, rot:  3,  scale: 1.02, z: 50, blur: 0,   bright: 1.00, op: 1.00 }, // FRONT
        { x: 24, y: 20, rot: -10, scale: 0.68, z: 10, blur: 1.4, bright: 0.78, op: 0.92 }, // top-left back
        { x: 80, y: 18, rot:  8,  scale: 0.70, z: 10, blur: 1.6, bright: 0.74, op: 0.90 }, // top-right back
        { x: 30, y: 80, rot: -8,  scale: 0.74, z: 25, blur: 0.6, bright: 0.88, op: 0.96 }, // bottom-left mid
        { x: 80, y: 82, rot:  9,  scale: 0.76, z: 25, blur: 0.8, bright: 0.86, op: 0.96 }  // bottom-right mid
      ];

      // Deck position lives in CSS (.mt-hero__float-deck) — that's what lets
      // mobile media queries shrink + reposition the deck. Don't set inline
      // styles here; they'd beat the breakpoint rules.

      // assignment[slotIdx] = card index occupying that slot
      var assignment = [0, 1, 2, 3, 4];
      // Cycle queue — which back slot promotes to front next
      var queue = [3, 2, 4, 1];
      var timer = null;

      function render() {
        cards.forEach(function (card, cardIdx) {
          var slotIdx = assignment.indexOf(cardIdx);
          var s = slots[slotIdx];
          var isFront = slotIdx === 0;
          card.style.left = s.x + '%';
          card.style.top  = s.y + '%';
          card.style.transform = 'translate(-50%, -50%) rotate(' + s.rot + 'deg) scale(' + s.scale + ')';
          card.style.zIndex = s.z;
          card.style.opacity = s.op;
          card.style.filter = 'blur(' + s.blur + 'px) brightness(' + s.bright + ') drop-shadow(0 ' +
            (isFront ? 38 : 22) + 'px ' + (isFront ? 46 : 30) + 'px rgba(0,0,0,' +
            (isFront ? 0.6 : 0.42) + '))';
          card.classList.toggle('is-front', isFront);
          card.setAttribute('aria-hidden', isFront ? 'false' : 'true');
        });
      }

      function advance() {
        var fromSlot = queue.shift();
        queue.push(fromSlot);
        var tmp = assignment[0];
        assignment[0] = assignment[fromSlot];
        assignment[fromSlot] = tmp;
        render();
      }

      function scheduleNext() {
        if (timer) clearTimeout(timer);
        timer = setTimeout(function () { advance(); scheduleNext(); }, 5000);
      }

      render();
      scheduleNext();

      root.addEventListener('click', function () { advance(); scheduleNext(); });
      root.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          advance();
          scheduleNext();
        }
      });
    });
  });

  // ========================================================================
  // Hero DUO PAIRS variant — matched MT/GZ archetype pair cycle
  //
  // Two stacks (MT left, GZ right) each hold the same 6 cards. One pair is
  // featured at a time; every 4.6s the stacks advance to the next archetype.
  // Front card crisp; the rest fan out / fade behind. The arcana label above
  // swaps to match. Click or Enter advances and resets the timer. Slot
  // transforms mirror duoSlotStyle() in the React HeroRightDuoPairs preview.
  // ========================================================================
  onReady(function () {
    document.querySelectorAll('[data-mt-duo-pairs]').forEach(function (root) {
      var mtCards = Array.prototype.slice.call(root.querySelectorAll('.mt-hero__duo-stack[data-duo-side="mt"] .mt-hero__duo-stack-card'));
      var gzCards = Array.prototype.slice.call(root.querySelectorAll('.mt-hero__duo-stack[data-duo-side="gz"] .mt-hero__duo-stack-card'));
      var dots    = Array.prototype.slice.call(root.querySelectorAll('[data-duo-dot]'));
      var label   = root.querySelector('[data-duo-arcana]');
      var arcana  = (root.getAttribute('data-duo-arcana-list') || '').split('|');
      var L = mtCards.length;
      if (L < 2 || gzCards.length !== L) return;

      var idx = 0;
      var timer = null;

      // dir: -1 = left stack (MT, fans left) · +1 = right stack (GZ, fans right)
      function slot(cardIdx, dir) {
        var rel = (((cardIdx - idx) % L) + L) % L;
        if (rel === 0)     return { t: 'translate(0px,0px) rotate(0deg) scale(1)',                                 o: 1, z: 5, b: 1    };
        if (rel === 1)     return { t: 'translate(' + (15 * dir) + 'px,6px) rotate(' + (6 * dir) + 'deg) scale(0.985)',  o: 1, z: 4, b: 0.92 };
        if (rel === 2)     return { t: 'translate(' + (29 * dir) + 'px,13px) rotate(' + (12 * dir) + 'deg) scale(0.965)', o: 1, z: 3, b: 0.82 };
        if (rel === L - 1) return { t: 'translate(' + (135 * dir) + 'px,-8px) rotate(' + (20 * dir) + 'deg) scale(0.97)',  o: 0, z: 6, b: 1    };
        return                  { t: 'translate(' + (40 * dir) + 'px,20px) rotate(' + (16 * dir) + 'deg) scale(0.9)',   o: 0, z: 2, b: 0.8  };
      }

      function place(cards, dir) {
        cards.forEach(function (card, cardIdx) {
          var s = slot(cardIdx, dir);
          card.style.transform = s.t;
          card.style.opacity = s.o;
          card.style.zIndex = s.z;
          card.style.filter = 'brightness(' + s.b + ')';
        });
      }

      function render() {
        place(mtCards, -1);
        place(gzCards, 1);
        dots.forEach(function (d, i) { d.classList.toggle('is-active', i === idx); });
        if (label && arcana[idx]) {
          label.textContent = arcana[idx];
          // Re-trigger the fade-up each change.
          label.style.animation = 'none';
          void label.offsetWidth;
          label.style.animation = '';
        }
      }

      function advance() { idx = (idx + 1) % L; render(); }

      function scheduleNext() {
        if (timer) clearTimeout(timer);
        timer = setTimeout(function () { advance(); scheduleNext(); }, 4600);
      }

      render();
      scheduleNext();

      root.addEventListener('click', function () { advance(); scheduleNext(); });
      root.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); advance(); scheduleNext(); }
      });
    });
  });

  // ========================================================================
  // HTML escape helper
  // ========================================================================
  function escapeHtml(s) {
    if (s == null) return '';
    return String(s)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

})();

